/// <reference types="google.maps" />
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Agrodrive } from 'app/core/services/agrodrive.service';
import { AppSharedModule } from 'app/app-shared.module';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { switchMap, tap, catchError, finalize, delay, takeUntil } from 'rxjs/operators';
import { throwError, forkJoin, of, from, Subject, interval, merge } from 'rxjs';
import { GoogleMapsModule } from '@angular/google-maps';
import { ShapefileReader } from 'app/utils/shapefile-reader';
import { MapsLoaderService } from 'app/core/services/maps-loader.service';
import { GestaoArquivoCardImageComponent } from '../gestao-arquivo-card-image/gestao-arquivo-card-image.component';
import { CardImagePipe } from 'app/pipes/shared/card-image.pipe';
import { TaskContentTranslationsPipe } from 'app/pipes/shared/task-content-translations.pipe';
import { TimeoutEnum } from 'app/core/enum/timeout.enum';
import { validateRequiredExtensions, unzipFiles } from 'app/utils/zip-manager';
import { AgresSpinnerService } from 'app/shared/components/agres-spinner/agres-spinner.service';
import { SupportPageService } from 'app/core/services/support-page.service';
import {
  AGRODRIVE_TASK_SORT_OPTIONS,
  AgrodriveTaskSortOrder,
  DEFAULT_AGRODRIVE_TASK_SORT_ORDER,
  filterAndSortAgrodriveTasks,
} from 'app/utils/agrodrive-task-sort.utils';

type AgrodriveFileItem = {
  fileName: string;
  metadata?: Record<
    string,
    string | number | Record<string, object | string> | boolean
  >;
};

type AgrodriveObject = {
  id: string;
  userId: string;
  deviceId: string;
  deviceType: string;
  imageUrl?: string;
  tasks: AgrodriveTaskItem[];
  guides: AgrodriveFileItem[];
  boundaries: AgrodriveFileItem[];
  variableRateMaps: AgrodriveFileItem[];
  expanded?: boolean;
  tasksExpanded?: boolean;
  guidesExpanded?: boolean;
  boundariesExpanded?: boolean;
  variableRateMapsExpanded?: boolean;
};

type AgrodriveTaskItem = AgrodriveFileItem & {
  path?: string;
  metadata: {
    status?: string;
    createdAt?: string;
    completedAt?: string;
  };
};

type AgrodriveResponse = {
  data: {
    metadata: {
      tasks: {
        container: string;
        backgroundContainerImages: string;
        rasterContainerImages: string;
      };
      guides: {
        container: string;
        backgroundContainerImages: string;
        rasterContainerImages: string;
      };
      boundaries: {
        container: string;
        backgroundContainerImages: string;
        rasterContainerImages: string;
      };
      variableRateMaps: {
        container: string;
        backgroundContainerImages: string;
        rasterContainerImages: string;
      };
    }, items: AgrodriveObject[]
  };
};

type TaskCardClickHandler = (callback?: () => void) => void;
@Component({
  selector: 'app-agrodrive',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AppSharedModule,
    GestaoArquivoCardImageComponent,
    CardImagePipe,
    TaskContentTranslationsPipe,
    GoogleMapsModule,
  ],
  templateUrl: './agrodrive.component.html',
  styleUrls: ['./agrodrive.component.scss'],
})
export class AgrodriveComponent implements OnInit, OnDestroy {
  agrodriveApiResponse: AgrodriveResponse | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly stopPolling$ = new Subject<void>();
  private isPolling = false;
  private readonly POLL_INTERVAL_MS = 15_000;

  // Modal properties
  showUploadModal = false;
  currentStep: 'device-selection' | 'file-upload' = 'device-selection';
  selectedDevices: AgrodriveObject[] = [];
  selectedFiles: File[] = [];
  isUploading = false;
  isDragOver = false;
  fileExtensionsMap: Map<string, { extensions: string[]; isValid: boolean }> =
    new Map();

  // Move Modal properties
  showMoveModal = false;
  moveModalStep: 'file-info' | 'device-selection' = 'file-info';
  fileToMove: {
    fileName: string;
    category: 'guides' | 'boundaries' | 'variableRateMaps';
    sourceDevice: AgrodriveObject;
  } | null = null;
  moveTargetDevices: AgrodriveObject[] = [];
  isMoving = false;

  // Folder navigation
  readonly USER_CONTAINER_ID = 'user';
  readonly subcategories = ['tasks', 'guides', 'boundaries', 'variableRateMaps'] as const;
  readonly userOnlySubcategories = ['guides', 'boundaries', 'variableRateMaps'] as const;
  currentView: 'root' | 'device' | 'subcategory' = 'root';
  selectedDevice: AgrodriveObject | null = null;
  selectedSubcategory: 'tasks' | 'guides' | 'boundaries' | 'variableRateMaps' | null = null;

  // Side panel preview properties
  selectedPreviewFile: AgrodriveFileItem | null = null;
  showFullMap = false;
  searchQuery = '';
  readonly taskSortOptions = AGRODRIVE_TASK_SORT_OPTIONS;
  selectedTaskSortOrder: AgrodriveTaskSortOrder =
    DEFAULT_AGRODRIVE_TASK_SORT_ORDER;
  private filteredTasksCache: {
    tasks: AgrodriveTaskItem[];
    searchQuery: string;
    sortOrder: AgrodriveTaskSortOrder;
    result: AgrodriveTaskItem[];
  } | null = null;
  isLoadingPreview = false;
  googleMapsApiLoaded = false;
  previewPolygons: Array<{ paths: google.maps.LatLngLiteral[][]; fillColor: string; strokeColor: string; fillOpacity: number; strokeWeight: number }> = [];
  previewPolylines: Array<{ path: google.maps.LatLngLiteral[] }> = [];
  previewLegend: Array<{ color: string; label: string }> = [];
  previewStats: { avg: number | null; count: number; min: number | null; max: number | null; field: string | null } | null = null;
  previewFeatures: any[] = [];
  previewAvailableFields: string[] = [];
  selectedValueField: string | null = null;
  previewMapOptions: google.maps.MapOptions = {
    mapTypeId: 'satellite',
    disableDefaultUI: true,
    zoomControl: true,
    fullscreenControl: true,
    tilt: 0,
    center: { lat: -10.33, lng: -53.2 },
    zoom: 5,
  };
  private previewTilesLoaded = false;

  private readonly deviceImageCache = new Map<string, { url: string; cachedAt: number }>();
  private readonly IMAGE_CACHE_TTL_MS = 25 * 60 * 1000; // 25 min (presigned URLs expiram em 30 min)

  private readonly MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;
  private readonly REQUIRED_SHAPEFILE_EXTENSIONS = ['shp', 'dbf', 'shx'];

  readonly defaultDeviceImageSrc =
    'assets/images/support-page/products/default-product-tecomec.svg';
  private readonly mapsLoader = inject(MapsLoaderService);

  constructor(
    private readonly agrodriveService: Agrodrive,
    private readonly router: Router,
    private readonly toastNotificationService: ToastrService,
    public translateService: TranslateService,
    private readonly spinnerService: AgresSpinnerService,
    private readonly supportPageService: SupportPageService,
  ) {}

  ngOnInit(): void {
    this.translateService.get('agrodrive.loading-devices').subscribe(msg => {
      this.spinnerService.toShow(msg);
    });
    this.fetchAgrodrive();
    this.initGoogleMaps();
  }

  private initGoogleMaps(): void {
    this.mapsLoader.load().subscribe((loaded) => {
      this.googleMapsApiLoaded = loaded;
    });
  }

  private fetchAgrodrive() {
    let errorMessage: string = '';

    forkJoin({
      loadingMessage: this.translateService.get('agrodrive.loading-devices'),
      errorMessage: this.translateService.get(
        'agrodrive.loading-devices-error',
      ),
    })
      .pipe(
        tap(({ loadingMessage, errorMessage: errMsg }) => {
          this.spinnerService.toShow(loadingMessage);
          errorMessage = errMsg;
        }),
        switchMap(() =>
          this.agrodriveService.getAgrodrive().pipe(
            tap((response: AgrodriveResponse) => {
              this.agrodriveApiResponse = response;
              response?.data?.items?.forEach(device => this.loadDeviceImage(device));
              if (this.selectedDevice) {
                const updated = response?.data?.items?.find(
                  d => d.deviceId === this.selectedDevice?.deviceId
                );
                if (updated) {
                  this.selectedDevice = updated;
                }
              }
              this.managePolling();
            }),
          ),
        ),
        catchError((error) => {
          this.toastNotificationService.error(errorMessage, null, {
            timeOut: TimeoutEnum.MediumShort,
            positionClass: 'toast-top-full-width',
          });
          return throwError(() => error);
        }),
        finalize(() => {
          this.spinnerService.toHide();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private hasProcessingTasks(): boolean {
    return this.agrodriveApiResponse?.data?.items?.some(device =>
      device.tasks?.some(task => task.metadata?.status === 'processing')
    ) ?? false;
  }

  private managePolling(): void {
    if (this.hasProcessingTasks()) {
      if (!this.isPolling) {
        this.isPolling = true;
        interval(this.POLL_INTERVAL_MS)
          .pipe(
            takeUntil(merge(this.stopPolling$, this.destroy$)),
            switchMap(() => this.agrodriveService.getAgrodrive().pipe(catchError(() => of(null)))),
          )
          .subscribe((response: AgrodriveResponse | null) => {
            if (!response) return;
            this.agrodriveApiResponse = response;
            response?.data?.items?.forEach(device => this.loadDeviceImage(device));
            if (this.selectedDevice) {
              const updated = response.data?.items?.find(
                d => d.deviceId === this.selectedDevice?.deviceId
              );
              if (updated) this.selectedDevice = updated;
            }
            if (!this.hasProcessingTasks()) {
              this.stopPolling$.next();
              this.isPolling = false;
            }
          });
      }
    } else if (this.isPolling) {
      this.stopPolling$.next();
      this.isPolling = false;
    }
  }

  private normalizeDeviceTypeForImage(deviceType: string): string {
    return deviceType
      .toLowerCase()
      .replace(/([a-zA-Z])(\d)/g, '$1_$2');
  }

  private loadDeviceImage(device: AgrodriveObject): void {
    const imageName = this.normalizeDeviceTypeForImage(device.deviceType);
    const cached = this.deviceImageCache.get(imageName);
    if (cached && Date.now() - cached.cachedAt < this.IMAGE_CACHE_TTL_MS) {
      device.imageUrl = cached.url;
      return;
    }
    this.supportPageService.getPresignedUrl([`images/${imageName}.png`]).subscribe({
      next: (data: string[]) => {
        const url = data[0] ?? this.defaultDeviceImageSrc;
        this.deviceImageCache.set(imageName, { url, cachedAt: Date.now() });
        device.imageUrl = url;
      },
      error: () => { device.imageUrl = this.defaultDeviceImageSrc; },
    });
  }

  onDeviceImageError(event: Event, device: AgrodriveObject): void {
    const img = event.target as HTMLImageElement;
    img.src = this.defaultDeviceImageSrc;
    this.deviceImageCache.delete(this.normalizeDeviceTypeForImage(device.deviceType));
  }

  toggleDevice(agrodrive: AgrodriveObject): void {
    agrodrive.expanded = !agrodrive.expanded;
  }

  toggleSubcategory(agrodrive: AgrodriveObject, subcategory: 'tasks' | 'guides' | 'boundaries' | 'variableRateMaps'): void {
    const key = `${subcategory}Expanded` as 'tasksExpanded' | 'guidesExpanded' | 'boundariesExpanded' | 'variableRateMapsExpanded';
    (agrodrive)[key] = !(agrodrive)[key];
  }

  trackByDevice(_: number, agrodrive: AgrodriveObject): string {
    return agrodrive.deviceId;
  }

  trackBySubcategory(_: number, subcategory: 'tasks' | 'guides' | 'boundaries' | 'variableRateMaps'): string {
    return subcategory;
  }

  trackByEntry(index: number, entry: AgrodriveFileItem): string {
    return `${index}-${entry.fileName}`;
  }

  /**
   * Tracks a task by its path and file name, the pair the index keeps unique,
   * so searching or sorting moves the cards instead of rebuilding them (and
   * reloading their thumbnails).
   */
  trackByTask(entry: AgrodriveTaskItem): string {
    return `${entry.path ?? ''}/${entry.fileName}`;
  }

  get physicalDevices(): AgrodriveObject[] {
    return this.agrodriveApiResponse?.data?.items?.filter(
      d => d.deviceId !== this.USER_CONTAINER_ID
    ) ?? [];
  }

  get userContainer(): AgrodriveObject | null {
    return this.agrodriveApiResponse?.data?.items?.find(
      d => d.deviceId === this.USER_CONTAINER_ID
    ) ?? null;
  }

  get userContainerDevice(): AgrodriveObject {
    return this.userContainer ?? {
      id: this.USER_CONTAINER_ID,
      userId: '',
      deviceId: this.USER_CONTAINER_ID,
      deviceType: 'user',
      tasks: [],
      guides: [],
      boundaries: [],
      variableRateMaps: [],
    };
  }

  get isUserContainerSelected(): boolean {
    return this.selectedDevice?.deviceId === this.USER_CONTAINER_ID;
  }

  get activeSubcategories(): readonly string[] {
    return this.isUserContainerSelected ? this.userOnlySubcategories : this.subcategories;
  }

  get filteredFiles(): AgrodriveFileItem[] {
    const files = this.getDeviceSubcategoryFiles(this.selectedSubcategory ?? '');
    if (!this.searchQuery.trim()) return files;
    const q = this.searchQuery.toLowerCase();
    return files.filter(f => f.fileName.toLowerCase().includes(q));
  }

  /**
   * The tasks of the selected device that match the search, in the chosen
   * order. The template reads it on every change detection, so the result is
   * reused until the task list (replaced on each fetch and poll), the search or
   * the order changes.
   */
  get filteredTasks(): AgrodriveTaskItem[] {
    if (!this.selectedDevice?.tasks) {
      return [];
    }
    const tasks = this.selectedDevice.tasks;
    const cache = this.filteredTasksCache;
    if (
      cache?.tasks === tasks &&
      cache.searchQuery === this.searchQuery &&
      cache.sortOrder === this.selectedTaskSortOrder
    ) {
      return cache.result;
    }
    const result = filterAndSortAgrodriveTasks(
      tasks,
      this.searchQuery,
      this.selectedTaskSortOrder,
    );
    this.filteredTasksCache = {
      tasks,
      searchQuery: this.searchQuery,
      sortOrder: this.selectedTaskSortOrder,
      result,
    };
    return result;
  }

  // Modal methods
  openUploadModal(): void {
    this.showUploadModal = true;
    this.currentStep = 'device-selection';
    this.selectedDevices = [];
    this.selectedFiles = [];
  }

  closeUploadModal(): void {
    this.showUploadModal = false;
    this.currentStep = 'device-selection';
    this.selectedDevices = [];
    this.selectedFiles = [];
    this.fileExtensionsMap.clear();
    this.isUploading = false;
  }

  toggleDeviceSelection(device: AgrodriveObject): void {
    const index = this.selectedDevices.findIndex(d => d.deviceId === device.deviceId);
    if (index > -1) {
      this.selectedDevices.splice(index, 1);
    } else {
      this.selectedDevices.push(device);
    }
  }

  isDeviceSelected(device: AgrodriveObject): boolean {
    return this.selectedDevices.some(d => d.deviceId === device.deviceId);
  }

  proceedToFileSelection(): void {
    this.currentStep = 'file-upload';
  }

  backToDeviceSelection(): void {
    this.currentStep = 'device-selection';
    this.selectedFiles = [];
    this.fileExtensionsMap.clear();
    // Keep selectedDevices so user doesn't lose their selection
  }

  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      await this.processFiles(Array.from(input.files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      await this.processFiles(Array.from(files));
    }
  }

  private async processFiles(files: File[]): Promise<void> {
    this.selectedFiles = files;
    this.fileExtensionsMap.clear();
    await this.validateZipFiles();
  }

  private async validateZipFiles(): Promise<void> {
    const zipFiles = this.selectedFiles.filter((file) =>
      file.name.toLowerCase().endsWith('.zip'),
    );

    let currentTimeoutDuration: number = TimeoutEnum.MediumShort;
    for (const zipFile of zipFiles) {
      const result = await validateRequiredExtensions(
        zipFile,
        this.REQUIRED_SHAPEFILE_EXTENSIONS,
      );

      this.fileExtensionsMap.set(zipFile.name, {
        extensions: result.extensions,
        isValid: result.valid,
      });

      if (!result.valid) {
        currentTimeoutDuration += TimeoutEnum.Short;
        this.translateService
          .get('agrodrive.shapefile-missing-files', {
            fileName: zipFile.name,
            missingExtensions: result.missingExtensions.join(', '),
          })
          .subscribe((msg: string) => {
            this.toastNotificationService.warning(msg, null, {
              timeOut: currentTimeoutDuration,
              positionClass: 'toast-top-full-width',
            });
          });
      }
    }

    this.validateLooseShapefiles();
  }

  private validateLooseShapefiles(): void {
    const looseFiles = this.selectedFiles.filter((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      return this.REQUIRED_SHAPEFILE_EXTENSIONS.includes(ext);
    });

    if (looseFiles.length === 0) return;

    const groups = new Map<string, { files: File[]; extensions: string[] }>();
    for (const file of looseFiles) {
      const lastDot = file.name.lastIndexOf('.');
      const baseName = lastDot > -1 ? file.name.substring(0, lastDot) : file.name;
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

      if (!groups.has(baseName)) {
        groups.set(baseName, { files: [], extensions: [] });
      }
      const group = groups.get(baseName)!;
      group.files.push(file);
      group.extensions.push(ext);
    }

    let currentTimeoutDuration = TimeoutEnum.MediumShort;
    for (const [baseName, { files, extensions }] of groups) {
      const missingExtensions = this.REQUIRED_SHAPEFILE_EXTENSIONS.filter(
        (ext) => !extensions.includes(ext),
      );

      if (missingExtensions.length > 0) {
        for (const file of files) {
          this.fileExtensionsMap.set(file.name, { extensions, isValid: false });
        }

        currentTimeoutDuration += TimeoutEnum.Short;
        this.translateService
          .get('agrodrive.shapefile-missing-files', {
            fileName: baseName,
            missingExtensions: missingExtensions.join(', '),
          })
          .subscribe((msg: string) => {
            this.toastNotificationService.warning(msg, null, {
              timeOut: currentTimeoutDuration,
              positionClass: 'toast-top-full-width',
            });
          });
      }
    }
  }

  getFileExtensions(fileName: string): string[] {
    return this.fileExtensionsMap.get(fileName)?.extensions || [];
  }

  isZipFileValid(fileName: string): boolean {
    const fileInfo = this.fileExtensionsMap.get(fileName);
    return fileInfo ? fileInfo.isValid : true;
  }

  hasInvalidZipFiles(): boolean {
    for (const [, value] of this.fileExtensionsMap) {
      if (!value.isValid) {
        return true;
      }
    }
    return false;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getTotalFileSize(): number {
    return this.selectedFiles.reduce((total, file) => total + file.size, 0);
  }

  isFileSizeExceeded(): boolean {
    return this.getTotalFileSize() > this.MAX_FILE_SIZE_BYTES;
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  uploadFiles(): void {
    if (this.selectedDevices.length === 0 || this.selectedFiles.length === 0) {
      return;
    }

    if (this.isFileSizeExceeded()) {
      this.translateService
        .get('agrodrive.file-size-exceeded')
        .subscribe((msg: string) => {
          this.toastNotificationService.error(msg, null, {
            timeOut: TimeoutEnum.MediumShort,
            positionClass: 'toast-top-full-width',
          });
        });
      return;
    }

    this.isUploading = true;

    // Create FormData for file upload
    const formData = new FormData();
    // Add all selected device IDs
    this.selectedDevices.forEach(device => {
      formData.append('deviceIds', device.deviceId);
    });

    this.selectedFiles.forEach((file) => {
      formData.append('files', file);
    });

    this.agrodriveService.uploadFiles(formData).pipe(
      tap(() => this.closeUploadModal()),
      switchMap(() => this.translateService.get('global.alert.send-file-upload-success')),
      tap((msg: string) => {
        this.toastNotificationService.success(msg, null, {
          timeOut: TimeoutEnum.Medium,
          positionClass: 'toast-top-full-width',
        });
        this.isUploading = false;
      }),
      switchMap((msg) => of(msg).pipe(delay(3000))),
      tap(() => this.fetchAgrodrive()),
      catchError((error) => {
        this.translateService.get('global.alert.send-file-upload-error').subscribe((msg: string) => {
          this.toastNotificationService.error(msg, null, {
            timeOut: TimeoutEnum.MediumShort,
            positionClass: 'toast-top-full-width',
          });
          this.isUploading = false;
        });
        return throwError(() => error);
      })
    ).subscribe();
  }

  // Move Modal methods
  openMoveModal(
    entry: AgrodriveFileItem,
    category: string,
    sourceDevice: AgrodriveObject
  ): void {
    // Only allow moving for shapefile categories
    if (!['guides', 'boundaries', 'variableRateMaps'].includes(category)) {
      return;
    }

    this.fileToMove = {
      fileName: entry.fileName,
      category: category as 'guides' | 'boundaries' | 'variableRateMaps',
      sourceDevice
    };
    this.moveTargetDevices = [];
    this.moveModalStep = 'file-info';
    this.showMoveModal = true;
  }

  closeMoveModal(): void {
    this.showMoveModal = false;
    this.moveModalStep = 'file-info';
    this.fileToMove = null;
    this.moveTargetDevices = [];
    this.isMoving = false;
  }

  proceedToDeviceSelectionForMove(): void {
    this.moveModalStep = 'device-selection';
  }

  backToFileInfo(): void {
    this.moveModalStep = 'file-info';
    this.moveTargetDevices = [];
  }

  navigateToDevice(device: AgrodriveObject): void {
    this.selectedDevice = device;
    this.currentView = 'device';
  }

  navigateToSubcategory(subcategory: typeof this.subcategories[number]): void {
    this.selectedSubcategory = subcategory;
    this.currentView = 'subcategory';
    this.searchQuery = '';
    this.clearPreview();
  }

  navigateToRoot(): void {
    this.currentView = 'root';
    this.selectedDevice = null;
    this.selectedSubcategory = null;
    this.searchQuery = '';
    this.clearPreview();
  }

  navigateBackToDevice(): void {
    this.currentView = 'device';
    this.selectedSubcategory = null;
    this.searchQuery = '';
    this.clearPreview();
  }

  getSubcategoryCount(subcategory: string): number {
    if (!this.selectedDevice) return 0;
    const arr = this.selectedDevice[subcategory as keyof AgrodriveObject];
    return Array.isArray(arr) ? arr.length : 0;
  }

  getDeviceSubcategoryFiles(subcategory: string): AgrodriveFileItem[] {
    if (!this.selectedDevice) return [];
    return (this.selectedDevice[subcategory as keyof AgrodriveObject] as AgrodriveFileItem[]) || [];
  }

  onFileCardClick(entry: AgrodriveFileItem): void {
    if (this.selectedPreviewFile?.fileName === entry.fileName) {
      this.clearPreview();
      return;
    }

    this.selectedPreviewFile = entry;
    this.previewPolygons = [];
    this.previewPolylines = [];
    this.previewLegend = [];
    this.previewStats = null;
    this.previewTilesLoaded = false;
    this.isLoadingPreview = false;

    if (!entry.fileName.toLowerCase().endsWith('.zip')) return;
    if (!this.selectedSubcategory || !this.selectedDevice) return;

    this.isLoadingPreview = true;
    this.previewTilesLoaded = false;

    this.agrodriveService.getDownloadUrl(this.selectedDevice.deviceId, this.selectedSubcategory, entry.fileName).pipe(
      switchMap(({ url }) => from(fetch(url).then(r => r.arrayBuffer()))),
      switchMap(buffer => from(
        unzipFiles(new Blob([buffer]), { allowedExtensions: ['shp', 'dbf'] }).then(async files => {
          const shpFile = files.find(f => f.name.toLowerCase().endsWith('.shp'));
          const dbfFile = files.find(f => f.name.toLowerCase().endsWith('.dbf'));
          if (!shpFile) throw new Error('No .shp file found in archive');
          const reader = new ShapefileReader();
          const shp = await reader.openShapefile(shpFile);
          if (dbfFile) {
            const dbf = await reader.openDbfFile(dbfFile);
            return reader.linkDBFDataToShapefileData(shp, dbf);
          }
          return shp;
        })
      )),
      tap(features => {
        this.previewFeatures = features;
        if (this.selectedSubcategory === 'variableRateMaps') {
          const fields = new Set<string>();
          for (const f of features) {
            if (!f.properties) continue;
            for (const key of Object.keys(f.properties)) {
              if (typeof f.properties[key] === 'number') fields.add(key);
            }
          }
          this.previewAvailableFields = Array.from(fields);
          this.selectedValueField = this.previewAvailableFields[0] ?? null;
        }
        this.convertAndDisplayFeatures(features);
        this.isLoadingPreview = false;
      }),
      catchError(error => {
        this.isLoadingPreview = false;
        this.translateService.get('agrodrive.preview-error').subscribe(msg => {
          this.toastNotificationService.error(msg, null, {
            timeOut: TimeoutEnum.MediumShort,
            positionClass: 'toast-top-full-width',
          });
        });
        return throwError(() => error);
      })
    ).subscribe();
  }

  clearPreview(): void {
    this.selectedPreviewFile = null;
    this.previewPolygons = [];
    this.previewPolylines = [];
    this.previewLegend = [];
    this.previewStats = null;
    this.previewTilesLoaded = false;
    this.isLoadingPreview = false;
    this.previewFeatures = [];
    this.previewAvailableFields = [];
    this.selectedValueField = null;
  }

  onValueFieldChange(field: string): void {
    this.selectedValueField = field;
    this.previewTilesLoaded = false;
    this.convertAndDisplayFeatures(this.previewFeatures);
  }

  openFullMapModal(): void {
    this.showFullMap = true;
    this.previewTilesLoaded = false;
  }

  closeFullMapModal(): void {
    this.showFullMap = false;
  }

  onPreviewMapTilesLoaded(map: google.maps.Map): void {
    if (this.previewTilesLoaded) return;
    this.previewTilesLoaded = true;
    const bounds = new google.maps.LatLngBounds();
    for (const poly of this.previewPolygons) {
      for (const ring of poly.paths) {
        for (const point of ring) bounds.extend(point);
      }
    }
    for (const line of this.previewPolylines) {
      for (const point of line.path) bounds.extend(point);
    }
    if (!bounds.isEmpty()) map.fitBounds(bounds);
  }

  private convertAndDisplayFeatures(features: any[]): void {
    this.previewPolygons = [];
    this.previewPolylines = [];
    this.previewLegend = [];
    this.previewStats = null;
    if (!features?.length) return;

    const isVariableRate = this.selectedSubcategory === 'variableRateMaps';
    const defaultColor = '#3388ff';

    // For variable rate maps, use the selected field and compute min/max
    let valueField: string | null = null;
    let minVal = Infinity;
    let maxVal = -Infinity;

    if (isVariableRate) {
      valueField = this.selectedValueField;
      for (const feature of features) {
        const props = feature.properties;
        if (!props || !valueField) continue;
        if (typeof props[valueField] === 'number') {
          minVal = Math.min(minVal, props[valueField]);
          maxVal = Math.max(maxVal, props[valueField]);
        }
      }
      if (valueField && isFinite(minVal)) {
        this.previewLegend = this.buildLegend(minVal, maxVal, valueField);
      }

      const values: number[] = features
        .filter(f => f.properties && valueField && typeof f.properties[valueField!] === 'number')
        .map(f => f.properties[valueField!]);
      const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
      this.previewStats = {
        count: features.length,
        min: isFinite(minVal) ? minVal : null,
        max: isFinite(maxVal) ? maxVal : null,
        avg,
        field: valueField,
      };
    } else {
      this.previewStats = { count: features.length, min: null, max: null, avg: null, field: null };
    }

    for (const feature of features) {
      const geometry = feature.geometry ?? feature;
      if (!geometry?.type || !geometry?.coordinates) continue;

      const fillColor = isVariableRate && valueField && feature.properties
        ? this.getColorForValue(feature.properties[valueField] ?? minVal, minVal, maxVal)
        : defaultColor;

      const strokeColor = isVariableRate ? '#000000' : defaultColor;
      const fillOpacity = isVariableRate ? 0.494 : 0.7;
      const strokeWeight = 1;

      switch (geometry.type) {
        case 'Polygon':
          this.previewPolygons.push({
            paths: geometry.coordinates.map((ring: number[][]) => ring.map(([lng, lat]: number[]) => ({ lat, lng }))),
            fillColor, strokeColor, fillOpacity, strokeWeight,
          });
          break;
        case 'MultiPolygon':
          for (const poly of geometry.coordinates) {
            this.previewPolygons.push({
              paths: poly.map((ring: number[][]) => ring.map(([lng, lat]: number[]) => ({ lat, lng }))),
              fillColor, strokeColor, fillOpacity, strokeWeight,
            });
          }
          break;
        case 'LineString':
          this.previewPolylines.push({ path: geometry.coordinates.map(([lng, lat]: number[]) => ({ lat, lng })) });
          break;
        case 'MultiLineString':
          for (const line of geometry.coordinates) {
            this.previewPolylines.push({ path: line.map(([lng, lat]: number[]) => ({ lat, lng })) });
          }
          break;
      }
    }
  }

  private readonly VARIABLE_RATE_COLORS = [
    '#3F3FFF', // blue
    '#3FFDFF', // cyan
    '#3FFF3F', // green
    '#FDFF3F', // yellow
    '#FF9E3F', // orange
    '#FF3F3F', // red
  ];

  private getColorForValue(value: number, min: number, max: number): string {
    if (min === max) return this.VARIABLE_RATE_COLORS[0];
    const bandSize = (max - min) / 6;
    const index = Math.min(Math.floor((value - min) / bandSize), 5);
    return this.VARIABLE_RATE_COLORS[index];
  }

  private buildLegend(min: number, max: number, _field: string): Array<{ color: string; label: string }> {
    const bandSize = min === max ? 0 : (max - min) / 6;
    return this.VARIABLE_RATE_COLORS.map((color, i) => ({
      color,
      label: (min + i * bandSize).toFixed(2),
    }));
  }

  onFileMoveClick(entry: AgrodriveFileItem, event: Event): void {
    event.stopPropagation();
    if (this.selectedSubcategory && this.selectedDevice &&
        ['guides', 'boundaries', 'variableRateMaps'].includes(this.selectedSubcategory)) {
      this.openMoveModal(entry, this.selectedSubcategory, this.selectedDevice);
    }
  }

  onFileDownloadClick(entry: AgrodriveFileItem, event: Event): void {
    event.stopPropagation();
    if (!this.selectedSubcategory || !this.selectedDevice) return;

    this.agrodriveService.getDownloadUrl(
      this.selectedDevice.deviceId,
      this.selectedSubcategory,
      entry.fileName
    ).pipe(
      catchError((error) => {
        this.translateService.get('agrodrive.download-file-error').subscribe((msg: string) => {
          this.toastNotificationService.error(msg, null, {
            timeOut: TimeoutEnum.MediumShort,
            positionClass: 'toast-top-full-width',
          });
        });
        return throwError(() => error);
      })
    ).subscribe(({ url }) => {
      const a = document.createElement('a');
      a.href = url;
      a.download = entry.fileName;
      a.click();
    });
  }

  getAvailableTargetDevices(): AgrodriveObject[] {
    if (!this.agrodriveApiResponse?.data?.items || !this.fileToMove) {
      return [];
    }
    const physical = this.agrodriveApiResponse.data.items.filter(
      device => device.deviceId !== this.fileToMove?.sourceDevice.deviceId
        && device.deviceId !== this.USER_CONTAINER_ID
    );
    if (this.fileToMove.sourceDevice.deviceId === this.USER_CONTAINER_ID) {
      return physical;
    }
    return [this.userContainerDevice, ...physical];
  }

  toggleMoveTargetDeviceSelection(device: AgrodriveObject): void {
    const index = this.moveTargetDevices.findIndex(d => d.deviceId === device.deviceId);
    if (index > -1) {
      this.moveTargetDevices.splice(index, 1);
    } else {
      this.moveTargetDevices.push(device);
    }
  }

  isMoveTargetDeviceSelected(device: AgrodriveObject): boolean {
    return this.moveTargetDevices.some(d => d.deviceId === device.deviceId);
  }

  moveFile(): void {
    if (!this.fileToMove || this.moveTargetDevices.length === 0) {
      return;
    }

    this.isMoving = true;

    const moveRequest = {
      sourceDeviceId: this.fileToMove.sourceDevice.deviceId,
      targetDeviceIds: this.moveTargetDevices.map(d => d.deviceId),
      category: this.fileToMove.category,
      fileName: this.fileToMove.fileName
    };

    this.agrodriveService.moveFiles(moveRequest).pipe(
      tap(() => this.closeMoveModal()),
      switchMap(() => this.translateService.get('agrodrive.move-file-success')),
      tap((msg: string) => {
        this.toastNotificationService.success(msg, null, {
          timeOut: TimeoutEnum.Medium,
          positionClass: 'toast-top-full-width',
        });
        this.isMoving = false;
      }),
      switchMap((msg) => of(msg).pipe(delay(3000))),
      tap(() => this.fetchAgrodrive()),
      catchError((error) => {
        this.translateService.get('agrodrive.move-file-error').subscribe((msg: string) => {
          this.toastNotificationService.error(msg, null, {
            timeOut: TimeoutEnum.MediumShort,
            positionClass: 'toast-top-full-width',
          });
          this.isMoving = false;
        });
        return throwError(() => error);
      })
    ).subscribe();
  }

  getContinueButtonTitle(): string {
    const count = this.selectedDevices.length;
    const devicesLabel = this.translateService.instant(
      count !== 1 ? 'agrodrive.devices' : 'agrodrive.device',
    );
    return `${this.translateService.instant('agrodrive.continue')} (${count} ${devicesLabel})`;
  }


  handleEntryClick(
    entry: Record<
      'fileName',
      string | number
    > & {
      metadata: Record<'parsedTaskId' | 'status', string>;
    },
    subcategory: 'tasks' | 'guides' | 'boundaries' | 'variableRateMaps',
  ): void {
    if (
      subcategory === 'tasks' &&
      entry.metadata &&
      entry.metadata?.parsedTaskId &&
      entry.metadata?.status === 'completed'
    ) {
      const taskId = entry.metadata.parsedTaskId;
      this.router.navigate(['/gestao-operacao/resultado-navegacao'], {
        queryParams: { id: taskId },
      });
    }
  }

  getTaskCardClickHandler(
    entry: AgrodriveFileItem & {
      metadata?: { parsedTaskId?: string; status?: string };
    },
  ): TaskCardClickHandler | null {
    if (
      entry.metadata?.parsedTaskId &&
      entry.metadata?.status === 'completed'
    ) {
      return (callback?: () => void) => {
        this.router.navigate(['/gestao-operacao/resultado-navegacao'], {
          queryParams: { id: entry.metadata?.parsedTaskId },
        });
        if (callback) {
          callback();
        }
      };
    }
    return null;
  }
}
